import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { di } from '../../../../../../state/inversify.config';
import { AssessmentCreationViewService } from '../../../assessmentCreationView/assessmentCreation.view.service';
import { ModulesSpecsDomain } from '../modules.domain';
import { Notes } from '../../components/notes/notes.dumb';
import { Instructions } from '../../components/instructions/instructions.dumb';
import { SkipModule } from '../../components/skipModule/skipModule.smart';
import { AssessmentModules } from '../../modules';
import { wordsPresentedFromKeyValList } from '../immediateMemory/immediateMemory.helper';
import { DelayedRecallWordsList } from '../../components/wordsList/delayedRecallWordsList.dumb';
import { useCustomTranslation } from '../../../../../../translation/customTranslation';
import { Banner } from '../../../../../../components/banner/banner';

export function DelayedRecall({ domainService }: any) {
  const moduleSpecsDomain: ModulesSpecsDomain =
    di.get<ModulesSpecsDomain>(ModulesSpecsDomain);
  const { t } = useCustomTranslation('assessment');
  const [showTimer, setShowTimer] = useState(false);

  const assessmentCreationViewService: AssessmentCreationViewService =
    di.get<AssessmentCreationViewService>(AssessmentCreationViewService);
  const module = assessmentCreationViewService.useModuleSelector();

  const { delayedRecall, immediateMemory } = module;
  const moduleName: string = 'delayedRecall';

  const startDate: Date = immediateMemory?.timeImmediateMemoryCompleted;
  const endDate: Date = new Date();
  const timeElapsedSinceImmediateMemoryCompleted =
    immediateMemory?.timeImmediateMemoryCompleted
      ? Math.round(
          (endDate?.valueOf() - startDate?.valueOf()) / (1000 * 60), // converting time to mins as delayed recall needs to start 5 mins after immediate memory started
        )
      : 0;

  const timeToWaitForBeforeStartingDelayedRecall = 5;
  const totalMinutes =
    timeToWaitForBeforeStartingDelayedRecall >
    timeElapsedSinceImmediateMemoryCompleted
      ? timeToWaitForBeforeStartingDelayedRecall -
        timeElapsedSinceImmediateMemoryCompleted
      : timeElapsedSinceImmediateMemoryCompleted -
        timeToWaitForBeforeStartingDelayedRecall;

  const [minutes, setMinutes] = useState(
    timeToWaitForBeforeStartingDelayedRecall -
      timeElapsedSinceImmediateMemoryCompleted,
  );

  const [seconds, setSeconds] = useState(0);
  const { heading, instructions, note } =
    moduleSpecsDomain.delayedRecallExamConfig();

  const saveModule = (): void => {
    // In case Immediate Memory isn't defined at all, we need to set skipModuleFlag to true.
    let immediateMemorySkipModuleFlag: boolean = true;
    if (immediateMemory?.skipModuleFlag !== undefined) {
      immediateMemorySkipModuleFlag = immediateMemory?.skipModuleFlag;
    }

    assessmentCreationViewService.dispatchSetAssessmentModule({
      delayedRecall: {
        wordsPresentedKeyVal: immediateMemory?.wordsPresentedKeyVal,
        wordsPresented: wordsPresentedFromKeyValList(
          delayedRecall?.wordsPresentedKeyVal,
        ),
        wordsRecalledKeyVal: delayedRecall?.wordsRecalledKeyVal,
        wordsRecalled: delayedRecall?.wordsRecalled,
        noAnswersCorrect: delayedRecall?.noAnswersCorrect,
        delayedRecallClinicalNotes: delayedRecall?.delayedRecallClinicalNotes,
        skipModuleFlag: delayedRecall?.skipModuleFlag
          ? delayedRecall?.skipModuleFlag
          : immediateMemorySkipModuleFlag,
        skipModuleComments: delayedRecall?.skipModuleComments,
        TOTAL_WORD_COUNT: 10,
        NORMATIVE: 4,
      },
    });
  };

  const setTrialResponse = (value: any, noAnswersCorrect: boolean): void => {
    const valueExists: boolean = !!(
      delayedRecall?.wordsRecalledKeyVal &&
      value in delayedRecall?.wordsRecalledKeyVal
    );

    if (value) {
      assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
        moduleName,
        'noAnswersCorrect',
        false,
      );
    }

    // For the string array of values
    let wordsRecalledResponse: string[] = [];
    if (value) {
      if (delayedRecall?.wordsRecalled) {
        wordsRecalledResponse = [...delayedRecall?.wordsRecalled, value];
      } else {
        wordsRecalledResponse = [value];
      }
    }

    if (value && valueExists) {
      wordsRecalledResponse = wordsRecalledResponse.filter(
        (v: string): boolean => v !== value,
      );
    }

    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      'wordsRecalled',
      wordsRecalledResponse,
    );

    // For the key/value list
    let wordsRecalledKeyValResponse: any = {};

    if (value) {
      wordsRecalledKeyValResponse = {
        ...delayedRecall?.wordsRecalledKeyVal,
        [noAnswersCorrect ? 'noAnswersCorrect' : value]:
          noAnswersCorrect || value,
        wordsRecalled: wordsRecalledResponse,
      };
    }

    if (value && valueExists) {
      delete wordsRecalledKeyValResponse[value];
    }

    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      'wordsRecalledKeyVal',
      wordsRecalledKeyValResponse,
    );

    // reset skip module if it is checked
    moduleSpecsDomain.resetSkipModule(moduleName);
  };

  const setNotesComments = (key: string, value: string) => {
    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      key,
      value,
    );

    moduleSpecsDomain.resetSkipModule(moduleName);
  };

  const noAnswersCorrectResponse = (
    notTestedKey: string,
    notTestedValue: boolean,
  ) => {
    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      'wordsRecalledKeyVal',
      {},
    );

    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      'wordsRecalled',
      [],
    );

    assessmentCreationViewService.dispatchSetAssessmentModuleDetails(
      moduleName,
      'noAnswersCorrect',
      notTestedValue,
    );

    moduleSpecsDomain.resetSkipModule(moduleName);
  };

  useEffect(() => {
    setShowTimer(minutes >= 0);
    const interval = setInterval(() => {
      if (minutes === 0 && seconds === 0) {
        clearInterval(interval);
      } else {
        setSeconds((_seconds: number) => {
          if (minutes <= 0) {
            return 0;
          }
          return _seconds === 0 ? 59 : _seconds - 1;
        });
        setMinutes((_minutes: number) => {
          if (_minutes <= 0) {
            return 0;
          }
          return seconds === 0 ? _minutes - 1 : _minutes;
        });
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [minutes, seconds]);

  useEffect(() => {
    domainService.dispatchNextEnabledAndValidate(
      delayedRecall,
      AssessmentModules.DelayedRecall,
    );
    domainService.dispatchBackEnabled(true);
    saveModule();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    delayedRecall?.wordsRecalledKeyVal,
    delayedRecall?.noAnswersCorrect,
    delayedRecall?.skipModuleFlag,
  ]);

  const formatTimerText = (): string => {
    return `${minutes.toString()}:${seconds.toString().padStart(2, '0')}`;
  };

  if (
    immediateMemory?.skipModuleFlag === undefined ||
    immediateMemory?.skipModuleFlag === true
  ) {
    return (
      <>
        <Typography variant="h6" fontWeight="bold">
          {t(heading)}
        </Typography>
        <Banner
          severity="warning"
          elevation={2}
          title={t('DELAYED_RECALL_MUST_COMPLETE_IMMEDIATE_MEMORY_FIRST')}
          icon={<InfoOutlinedIcon />}
        />
        <SkipModule
          moduleKeyValue={moduleName}
          skipModuleFlag
          skipModuleComments={delayedRecall?.skipModuleComments}
        />
      </>
    );
  }

  return (
    <>
      <Typography variant="h6" fontWeight="bold">
        {t(heading)}
      </Typography>
      <Instructions instructions={instructions} />
      <Banner
        severity="info"
        elevation={2}
        title={t('DELAYED_RECALL_COUNTDOWN')}
        icon={<InfoOutlinedIcon />}
        subHeader={showTimer ? formatTimerText() : ''}
      />
      <DelayedRecallWordsList
        wordListKey="wordsRecalled"
        wordsPresented={delayedRecall?.wordsPresentedKeyVal}
        setResponse={setTrialResponse}
        onSaveNoAnswersCorrect={noAnswersCorrectResponse}
        data={delayedRecall?.wordsRecalled}
        noAnswersCorrect={delayedRecall?.noAnswersCorrect}
      />
      {note && (
        <Notes
          label={t(note.label)}
          keyValue={note.key}
          onSave={setNotesComments}
          data={delayedRecall?.delayedRecallClinicalNotes || ''}
          fullWidth
        />
      )}
      <SkipModule
        moduleKeyValue={moduleName}
        skipModuleFlag={delayedRecall?.skipModuleFlag}
        skipModuleComments={delayedRecall?.skipModuleComments}
      />
    </>
  );
}
